import {
  Dialog,
  DialogContent,
  DialogProps,
  DialogTitle,
  IconButton,
  makeStyles,
  Snackbar,
  TextareaAutosize,
  TextField,
  Typography,
} from "@material-ui/core"
import { Alert, Autocomplete, Color } from "@material-ui/lab"
import { toArray } from "lodash"
import React from "react"
import {
  Enum_Insight_Content_Type_Enum,
  useInsertResourceAccessAndResourceMutation,
  useUpdateResourceMutation,
} from "../../generated/graphql"
import { useGraphQLClient } from "../../hooks/useGraphQLClient"
import { useIsOrgAdmin } from "../../hooks/useIsOrgAdmin"
import { useInAppResources } from "../../hooks/useInAppResources"
import { useUser } from "../../hooks/useUser"
import { colors } from "../../utils/theme"
import { Resource } from "../../utils/types"
import Button, { GhostButton } from "../atoms/button/Button"
import CloseIcon from "../atoms/CloseIcon"

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: 0,
    right: 0,
    padding: theme.spacing(2),
  },
  title: {
    padding: theme.spacing(4, 4, 2),
  },
  contentRoot: {
    padding: theme.spacing(1, 4, 4),
  },
  tabButton: {
    backgroundColor: colors.primary.ice_03,
    opacity: 1,
    color: colors.primary.midnight,
    textTransform: "capitalize",
    minHeight: 50,
    "&.Mui-selected": {
      backgroundColor: colors.primary.ice,
    },
    "&:first-child": {
      borderTopLeftRadius: 100,
      borderBottomLeftRadius: 100,
    },
    "&:last-child": {
      borderTopRightRadius: 100,
      borderBottomRightRadius: 100,
    },
    "&.MuiTab-root": {
      minWidth: "unset",
    },
  },
  tabPanel: {
    display: "grid",
    gap: theme.spacing(3),
    padding: theme.spacing(3, 0),
  },
  domainLabel: {
    display: "flex",
    justifyContent: "center",
  },
  domainDot: {
    height: 10,
    width: 10,
    margin: 8,
    borderRadius: 20,
    display: "inline-block",
  },
  pickerContainer: {
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 12,
    padding: theme.spacing(5, 3, 4),
    position: "relative",
    margin: theme.spacing(3, 3, 0, 3),
    display: "grid",
    gap: theme.spacing(4),
  },
  pickerTabsContainer: {
    // width offset for pickerContainer margin
    width: `calc(100% - ${theme.spacing(6)}px)`,
    position: "absolute",
    left: theme.spacing(3),
    top: -30,
    border: `4px solid ${colors.neutral.white}`,
    borderRadius: 150,
    backgroundColor: colors.neutral.white,
  },
  pickerBody: {
    display: "grid",
    gap: theme.spacing(4),
  },
  dayPicker: {
    paddingTop: theme.spacing(2),
    display: "grid",
    gridAutoFlow: "column",
    gridAutoColumns: "1fr",
  },
  cancelContainer: {
    marginTop: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  dayCheckbox: {
    borderColor: colors.neutral.clay,
    borderWidth: 1,
    borderStyle: "solid",
    borderLeftWidth: 0,
    borderRadius: 0,
    "&:first-child": {
      borderLeftWidth: 1,
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
    },
    "&:last-child": {
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4,
    },

    "&:hover": {
      backgroundColor: colors.primary.ice_03,
    },
    "&.Mui-checked": {
      backgroundColor: colors.primary.ice,
    },
    "&.Mui-checked:hover": {
      backgroundColor: colors.primary.periwinkle,
    },
    "&:active": {
      backgroundColor: colors.primary.periwinkle,
    },
    "&.Mui-focusVisible": {
      border: `2px solid ${colors.primary.periwinkle}`,
    },
  },
  dayCheckboxLabel: {
    color: colors.primary.shadow,
    fontWeight: "bold",
  },
  focusVisible: {
    "&.Mui-focusVisible": {
      boxShadow: `0 0 0 2px ${colors.primary.periwinkle}`,
    },
  },
}))

interface ScheduleDialogProps extends DialogProps {
  item?: Resource
  newResource?: boolean
}

export const InAppResourceDialog: React.FC<ScheduleDialogProps> = ({
  newResource = false,
  item,
  ...props
}) => {
  const classes = useStyles()
  const { user } = useUser()
  const graphqlClient = useGraphQLClient()
  //states
  const [title, setTitle] = React.useState<string | undefined>(undefined)
  const [content, setContent] = React.useState<string | undefined>(undefined)
  const [headerImage, setheaderImage] = React.useState<string | undefined>(
    undefined
  )
  const [author, setAuthor] = React.useState<string | undefined>(undefined)
  const [contentType, setContentType] =
    React.useState<Enum_Insight_Content_Type_Enum | null>(null)
  //mutations
  const { refetchAllResources, refetchOrgResources } = useInAppResources(
    user.organizations ? user?.organizations[0]?.id : -1
  )
  const onSuccess = () => {
    refetchAllResources()
    refetchOrgResources()
  }
  const {
    mutate: insertResourceWithAccess,
    error: errorInsertResourceWithAccess,
  } = useInsertResourceAccessAndResourceMutation(graphqlClient, { onSuccess })
  const { mutate: updateResource } = useUpdateResourceMutation(graphqlClient, {
    onSuccess,
  })
  const isOrgAdmin = useIsOrgAdmin()
  const isKsana = isOrgAdmin && user?.email?.endsWith("@ksanahealth.com")
  const getContentTypeArray = (): any[] => {
    const array = toArray(Enum_Insight_Content_Type_Enum) as any[]
    array.splice(0, 0, "")
    return array
  }
  const [snackbar, setSnackbar] = React.useState<{
    open: boolean
    severity: Color | undefined
    message: string | undefined
  }>({
    open: false,
    severity: undefined,
    message: undefined,
  })

  const handleCloseSnackbar = (
    event?: React.SyntheticEvent,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return
    }
    setSnackbar({ open: false, severity: undefined, message: undefined })
  }

  const save = async () => {
    try {
      if (!user) {
        throw new Error("User not set")
      }
      if (title === undefined) {
        throw new Error("No title set")
      }
      if (content === "" && content === undefined) {
        throw new Error("No content added")
      }
      if (contentType === undefined) {
        throw new Error("No content type set")
      }
      if (user.organizations?.[0].id === undefined) {
        throw new Error("Not part of an organization")
      }
      const itemId = item?.id
      if (content && contentType) {
        item = {
          id: itemId ?? -1,
          created_by: user.id,
          created_on: undefined,
          owner_org_id: user.organizations?.[0].id,
          content: content,
          content_type: contentType,
          header_image: headerImage,
          author: author,
          title: title,
          public: item?.public,
          updated_on: undefined,
        }
      }
      if (item) {
        if (
          (item.created_by && item.created_by === user.id && !newResource) ||
          (!item.created_by && isKsana() && !newResource)
        ) {
          //edit a current resource
          delete item.created_by
          delete item.owner_org_id
          updateResource(item)
        } else {
          //insert new resource
          insertResourceWithAccess({
            created_by: isKsana ? null : item.created_by,
            owner_org_id: user.organizations?.[0].id,
            author: item.author,
            content: item.content,
            content_type: item.content_type,
            header_image: item.header_image,
            public: isKsana ? true : false,
            title: title,
          })
          if (await errorInsertResourceWithAccess) {
            throw new Error("Unable to save resource.")
          }
        }
      } else {
        throw new Error("No resource object found")
      }

      setSnackbar({
        open: true,
        severity: "success",
        message: newResource ? "Added!" : "Updated!",
      })

      // in order to fire the onClose, it needs garbage arguments
      props.onClose?.({}, "backdropClick")
    } catch (error) {
      setSnackbar({
        open: true,
        severity: "error",
        message: (error as unknown as { message: string }).message,
      })
      console.error("schedule error", error)
    }
  }

  // pre-fill with item
  React.useEffect(() => {
    if (!item) {
      return
    }
    setTitle(item?.title ?? undefined)
    setContent(item.content ?? undefined)
    setContentType(item.content_type ?? undefined)
    setAuthor(item.author ?? undefined)
    setheaderImage(item.header_image ?? undefined)
  }, [item])

  React.useEffect(() => {
    if (!props.open) {
      // clear state on close
      // TODO: use initial tab

      setTitle(undefined)
      setContent("")
      setContentType(null)
      setAuthor(undefined)
      setheaderImage(undefined)
    }
  }, [props.open])

  return (
    <>
      <Dialog {...props} fullWidth maxWidth="sm">
        {/* Sort of a hack, but I have to give the `onClose` arguments to work :shrug: */}
        <IconButton
          className={classes.closeButton}
          onClick={(e) => {
            // setEnableScheduledItemTab(true)
            props.onClose?.(e, "backdropClick")
          }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle disableTypography className={classes.title}>
          <Typography variant="h3" component="h2">
            {newResource ? "Add New" : "Edit"} Resource
          </Typography>
        </DialogTitle>
        <DialogContent className={classes.contentRoot}>
          {/* Nudge Tab */}
          <div className={classes.tabPanel}>
            <TextField
              label="Title"
              variant="outlined"
              id="title"
              value={title}
              onChange={(e) => {
                const text = e.target.value
                if (text) {
                  setTitle(text)
                } else {
                  setTitle(undefined)
                }
              }}
              fullWidth
            />
            <TextField
              label="Header Image"
              variant="outlined"
              id="header_image"
              value={headerImage}
              onChange={(e) => {
                const text = e.target.value
                if (text) {
                  setheaderImage(text)
                } else {
                  setheaderImage(undefined)
                }
              }}
              fullWidth
            />
            <TextField
              label="Author / Source"
              variant="outlined"
              id="author_source"
              value={author}
              onChange={(e) => {
                const text = e.target.value
                if (text) {
                  setAuthor(text)
                } else {
                  setAuthor(undefined)
                }
              }}
              fullWidth
            />
            <Autocomplete
              id="content-type"
              value={contentType}
              options={getContentTypeArray() ?? []}
              getOptionLabel={(option) => option.toUpperCase()}
              onChange={(_, value) => {
                setContentType(value)
              }}
              getOptionSelected={(option, value) => option !== null}
              autoSelect
              autoHighlight
              fullWidth
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Content Type"
                  variant="outlined"
                />
              )}
            />
            <TextareaAutosize
              aria-label="Content TextArea"
              minRows={10}
              placeholder="In-App Content"
              style={{ width: "100%" }}
              value={content}
              onChange={(e) => {
                setContent(e.target.value ?? undefined)
              }}
            />
          </div>
          <br />
          <Button
            onClick={save}
            customVariant="primary"
            fullWidth
            // disabled={item?.created_by === null}
          >
            Save
          </Button>
          <div className={classes.cancelContainer}>
            <GhostButton
              onClick={(e) => {
                props.onClose?.(e, "backdropClick")
              }}
            >
              Cancel
            </GhostButton>
          </div>
        </DialogContent>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}
